/**
 * CampaignLanding — auto-generated public landing page for a campaign.
 *
 * Slug-routed at `/c/:slug`. Calls GET /api/public/campaigns/{slug}.
 * Renders 404 cleanly if the campaign isn't published.
 *
 * Layout matches the Ekselens brand kit: black hero strip, single purple
 * accent, Work Sans typography, single CTA, brand watermark star.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import api from "../lib/api";
import StarMotif from "../components/StarMotif";
import PageLoader from "../components/PageLoader";
import NotFound from "./NotFound";

export default function CampaignLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/public/campaigns/${slug}`)
      .then((r) => { if (!cancelled) setCampaign(r.data); })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return <PageLoader testid="campaign-loading" />;
  }
  if (notFound || !campaign) return <NotFound />;

  const ctaTarget = campaign.cta_target?.startsWith("http")
    ? campaign.cta_target
    : campaign.cta_target || "/assessments";

  const onCta = (e) => {
    if (ctaTarget.startsWith("http")) return; // let <a> handle it
    e.preventDefault();
    navigate(ctaTarget);
  };

  return (
    <article data-testid={`campaign-landing-${campaign.slug}`}>
      {/* Hero */}
      <section className="relative bg-black text-white overflow-hidden">
        <div className="absolute -right-32 -top-20 opacity-20 pointer-events-none">
          <StarMotif size={520} color="#7737F6" />
        </div>
        <div className="ek-container py-24 md:py-32 relative">
          <div className="ek-eyebrow text-[#A179FA] inline-flex items-center gap-2">
            <Sparkles size={12} /> Campaign · {campaign.framework_id}
          </div>
          <h1
            className="ek-heading text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-4xl"
            data-testid="campaign-hero-title"
          >
            {campaign.hero_title}
          </h1>
          {campaign.hero_subtitle && (
            <p
              className="text-zinc-300 text-base md:text-lg mt-6 max-w-2xl"
              data-testid="campaign-hero-subtitle"
            >
              {campaign.hero_subtitle}
            </p>
          )}
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href={ctaTarget}
              onClick={onCta}
              className="ek-btn bg-[#7737F6] text-white inline-flex items-center gap-2 px-7 py-3.5 text-[12px] tracking-[0.16em] uppercase hover:opacity-90"
              data-testid="campaign-cta-primary"
            >
              {campaign.cta_label} <ArrowRight size={14} />
            </a>
            <a
              href="/contact"
              className="text-white/80 hover:text-white text-[12px] tracking-[0.16em] uppercase border border-white/30 hover:border-white px-6 py-3 transition-colors"
              data-testid="campaign-cta-secondary"
            >
              Book a discovery conversation
            </a>
          </div>
        </div>
      </section>

      {/* Body */}
      {campaign.body_md && (
        <section className="bg-white">
          <div className="ek-container py-20 md:py-24">
            <div
              className="prose prose-zinc max-w-3xl whitespace-pre-line text-[15px] leading-relaxed"
              data-testid="campaign-body"
            >
              {campaign.body_md}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA strip */}
      <section className="bg-[#F2F2F3]">
        <div className="ek-container py-16 flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="ek-eyebrow text-[#7737F6]">Ready when you are</div>
            <h3 className="ek-heading text-2xl mt-2">{campaign.cta_label}</h3>
            <p className="text-zinc-600 text-[14px] mt-1">
              Runs in your browser. No software install. Results in minutes.
            </p>
          </div>
          <a
            href={ctaTarget}
            onClick={onCta}
            className="ek-btn bg-black text-white inline-flex items-center gap-2 px-7 py-3.5 text-[12px] tracking-[0.16em] uppercase hover:opacity-90"
            data-testid="campaign-cta-footer"
          >
            {campaign.cta_label} <ArrowRight size={14} />
          </a>
        </div>
      </section>
    </article>
  );
}
